import React from 'react';
import { Container, Typography, Box, Paper, Divider } from '@mui/material';
// import { useTheme } from '@mui/material/styles'; // Not used

const PrivacyPolicy: React.FC = () => {
  // const theme = useTheme(); // Not used

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h3" component="h1" gutterBottom align="center">
          Privacy Policy
        </Typography>
        <Typography variant="subtitle1" gutterBottom align="center" color="text.secondary">
          Last Updated: January 15, 2025
        </Typography>
        
        <Divider sx={{ my: 3 }} />

        <Box sx={{ '& h2': { mt: 4, mb: 2 }, '& h3': { mt: 3, mb: 1 }, '& p, & ul': { mb: 2 } }}>
          <Typography variant="h5" component="h2">
            1. Introduction
          </Typography>
          <Typography variant="body1" paragraph>
            Welcome to Health-Tree ("we," "our," or "us"). We are committed to protecting your personal information and your right to privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our personal development application.
          </Typography>

          <Typography variant="h5" component="h2">
            2. Information We Collect
          </Typography>
          
          <Typography variant="h6" component="h3">
            2.1 Personal Information You Provide
          </Typography>
          <ul>
            <li><strong>Account Information</strong>: Email address, password (encrypted), and username</li>
            <li><strong>Profile Information</strong>: Personal development goals, progress data, and talent tree selections</li>
            <li><strong>Payment Information</strong>: Processed securely through Stripe (we do not store credit card details)</li>
          </ul>

          <Typography variant="h6" component="h3">
            2.2 Automatically Collected Information
          </Typography>
          <ul>
            <li><strong>Usage Data</strong>: How you interact with our application, features you use, and time spent</li>
            <li><strong>Device Information</strong>: Browser type, operating system, and device identifiers</li>
            <li><strong>Analytics Data</strong>: Through Vercel Analytics to improve our services</li>
          </ul>

          <Typography variant="h5" component="h2">
            3. How We Use Your Information
          </Typography>
          <Typography variant="body1" paragraph>
            We use your information to:
          </Typography>
          <ul>
            <li>Provide and maintain our services</li>
            <li>Process your premium subscription payments</li>
            <li>Track your personal development progress</li>
            <li>Send email confirmations and important updates</li>
            <li>Improve our application and user experience</li>
            <li>Comply with legal obligations</li>
          </ul>

          <Typography variant="h5" component="h2">
            4. Information Sharing and Disclosure
          </Typography>
          <Typography variant="body1" paragraph>
            We do not sell, trade, or rent your personal information. We may share your information only in the following situations:
          </Typography>

          <Typography variant="h6" component="h3">
            4.1 Service Providers
          </Typography>
          <ul>
            <li><strong>Supabase</strong>: For authentication, database storage, and serverless functions</li>
            <li><strong>Stripe</strong>: For payment processing (they have their own privacy policy)</li>
            <li><strong>Vercel</strong>: For hosting and analytics</li>
          </ul>

          <Typography variant="h6" component="h3">
            4.2 Legal Requirements
          </Typography>
          <Typography variant="body1" paragraph>
            We may disclose your information if required by law or in response to valid legal requests.
          </Typography>

          <Typography variant="h5" component="h2">
            5. Data Security
          </Typography>
          <Typography variant="body1" paragraph>
            We implement appropriate technical and organizational security measures to protect your personal information, including:
          </Typography>
          <ul>
            <li>Encryption of passwords and sensitive data</li>
            <li>Secure HTTPS connections</li>
            <li>Regular security audits</li>
            <li>Limited access to personal information</li>
          </ul>

          <Typography variant="h5" component="h2">
            6. Your Rights and Choices
          </Typography>
          <Typography variant="body1" paragraph>
            You have the right to:
          </Typography>
          <ul>
            <li>Access and update your personal information</li>
            <li>Delete your account and associated data</li>
            <li>Opt-out of non-essential communications</li>
            <li>Export your personal development data</li>
            <li>Cancel your subscription at any time</li>
          </ul>

          <Typography variant="h5" component="h2">
            7. Data Retention
          </Typography>
          <Typography variant="body1" paragraph>
            We retain your personal information for as long as necessary to provide our services and comply with legal obligations. When you delete your account, we will delete or anonymize your personal information within 30 days.
          </Typography>

          <Typography variant="h5" component="h2">
            8. Children's Privacy
          </Typography>
          <Typography variant="body1" paragraph>
            Our service is not intended for users under 13 years of age. We do not knowingly collect personal information from children under 13.
          </Typography>

          <Typography variant="h5" component="h2">
            9. International Data Transfers
          </Typography>
          <Typography variant="body1" paragraph>
            Your information may be transferred and processed in countries other than your own. We ensure appropriate safeguards are in place to protect your information.
          </Typography>

          <Typography variant="h5" component="h2">
            10. Updates to This Policy
          </Typography>
          <Typography variant="body1" paragraph>
            We may update this Privacy Policy from time to time. We will notify you of any material changes by email or through the application.
          </Typography>

          <Typography variant="h5" component="h2">
            11. Contact Us
          </Typography>
          <Typography variant="body1" paragraph>
            If you have questions about this Privacy Policy, please contact us at:
          </Typography>
          <ul>
            <li>Email: privacy@health-tree.com</li>
          </ul>

          <Typography variant="h5" component="h2">
            12. California Privacy Rights
          </Typography>
          <Typography variant="body1" paragraph>
            California residents have additional rights under the California Consumer Privacy Act (CCPA), including the right to:
          </Typography>
          <ul>
            <li>Know what personal information is collected</li>
            <li>Know whether personal information is sold or disclosed</li>
            <li>Say no to the sale of personal information</li>
            <li>Access personal information</li>
            <li>Request deletion of personal information</li>
            <li>Not be discriminated against for exercising privacy rights</li>
          </ul>

          <Typography variant="h5" component="h2">
            13. Cookie Policy
          </Typography>
          <Typography variant="body1" paragraph>
            We use essential cookies to maintain your session and preferences. Analytics cookies are used through Vercel Analytics to understand usage patterns. You can control cookies through your browser settings.
          </Typography>

          <Typography variant="h5" component="h2">
            14. Third-Party Links
          </Typography>
          <Typography variant="body1" paragraph>
            Our application may contain links to third-party websites. We are not responsible for the privacy practices of these external sites.
          </Typography>

          <Typography variant="body1" paragraph sx={{ mt: 4, fontStyle: 'italic' }}>
            By using Health-Tree, you agree to the collection and use of information in accordance with this Privacy Policy.
          </Typography>
        </Box>
      </Paper>
    </Container>
  );
};

export default PrivacyPolicy;